import { limitVolumeTiles, MAX_POINTS_PER_CHUNK, MAX_VISIBLE_POINTS, MAX_VOLUME_TILES, chunkItems } from './render-guards';
import { selectAtlasLOD } from './atlas-layer';

/**
 * V2-30 benchmark contract.  Keep these budgets in one place so a report
 * cannot accidentally compare a result with a different acceptance target.
 */
export const BENCHMARK_BUDGETS = Object.freeze({
  firstSpatialResultP95Ms: 1000,
  selectionFeedbackP95Ms: 100,
  summaryLoadingP95Ms: 500,
  maxVisiblePoints: MAX_VISIBLE_POINTS,
  maxPointsPerChunk: MAX_POINTS_PER_CHUNK,
  maxVolumeTiles: MAX_VOLUME_TILES,
});

export const BROWSER_MATRIX = Object.freeze([
  { browser: 'Chrome', versions: 'latest-2' },
  { browser: 'Firefox', versions: 'latest-2' },
  { browser: 'Safari', versions: 'latest-2' },
  { browser: 'Edge', versions: 'latest-2' },
]);

export const DEFAULT_BENCHMARK_FIXTURE = Object.freeze({
  datasetId: 'ds-visual-cortex-01',
  version: '2026-01',
  entityCount: 100000,
  coordinateSpaceId: 'ccf-v3',
});

const now = (clock) => clock?.now?.() ?? Date.now();

/** Return the nearest-rank p95 without hiding small-sample failures. */
export function percentile(samples = [], percentileValue = 0.95) {
  const values = samples.filter((value) => typeof value === 'number' && Number.isFinite(value)).sort((a, b) => a - b);
  if (!values.length) return null;
  const rank = Math.max(0, Math.ceil(values.length * percentileValue) - 1);
  return values[rank];
}

export function budgetFor(name) {
  return BENCHMARK_BUDGETS[`${name}P95Ms`] ?? null;
}

async function measure(name, operation, { clock = typeof performance !== 'undefined' ? performance : null } = {}) {
  const started = now(clock);
  try {
    await operation();
    return { name, durationMs: Math.max(0, now(clock) - started) };
  } catch (error) {
    return { name, durationMs: Math.max(0, now(clock) - started), error: 'operation_failed' };
  }
}

function summary(name, measurements) {
  const durations = measurements.map((item) => item.durationMs);
  const p95Ms = percentile(durations);
  const budgetMs = budgetFor(name);
  return {
    samples: durations.length,
    p95Ms,
    budgetMs,
    pass: p95Ms !== null && !measurements.some(item => item.error) && (budgetMs === null || p95Ms <= budgetMs),
    errors: measurements.filter((item) => item.error).map((item) => item.error),
  };
}

function fixtureEntities(count = DEFAULT_BENCHMARK_FIXTURE.entityCount) {
  return Array.from({ length: count }, (_, index) => ({
    entity_id: `benchmark:entity:${index}`,
    coordinate_space_id: DEFAULT_BENCHMARK_FIXTURE.coordinateSpaceId,
    position: [index % 1000, Math.floor(index / 1000) % 100, Math.floor(index / 100000)],
  }));
}

/** Benchmark the client-facing flows and bounded derivative work separately. */
export async function runNeuroVizBenchmark({
  client,
  fixture = DEFAULT_BENCHMARK_FIXTURE,
  iterations = 20,
  entities = null,
  meshes = [],
  volumeTiles = [],
  clock = typeof performance !== 'undefined' ? performance : null,
  environment = typeof navigator !== 'undefined' ? navigator : null,
} = {}) {
  if (!client) throw new TypeError('A NeuroVizApiClient-compatible client is required.');
  if (!Number.isInteger(iterations) || iterations < 1 || iterations > 300) throw new RangeError('Benchmark iterations must be 1..300');
  const count = iterations;
  const metadataMeasurements = [];
  const spatialMeasurements = [];
  const pointMeasurements = [];
  const derivativeMeasurements = [];
  const evidenceMeasurements = [];
  const entityRows = entities || fixtureEntities(fixture.entityCount);
  const entityId = entityRows[0]?.entity_id || `${fixture.datasetId}:${fixture.version}:neuron:c0001`;

  for (let index = 0; index < count; index += 1) {
    metadataMeasurements.push(await measure('metadata', async () => {
      const catalog = await client.getDatasetCatalog();
      const dataset = catalog.datasets?.find((item) => item.dataset_id === fixture.datasetId) || catalog.datasets?.[0];
      return client.getManifest(dataset?.dataset_id || fixture.datasetId, dataset?.version || fixture.version);
    }, { clock }));
    spatialMeasurements.push(await measure('firstSpatialResult', () => client.querySpatialEntities({
      dataset_id: fixture.datasetId,
      version: fixture.version,
      coordinate_space_id: fixture.coordinateSpaceId,
      page: 1,
      page_size: 25,
    }), { clock }));
    pointMeasurements.push(await measure('selectionFeedback', () => {
      const selected = entityRows[index % entityRows.length];
      return chunkItems(entityRows, { maxItems: MAX_VISIBLE_POINTS, chunkSize: MAX_POINTS_PER_CHUNK, getPosition: (item) => item.position }).find((chunk) => chunk.includes(selected));
    }, { clock }));
    derivativeMeasurements.push(await measure('summaryLoading', () => ({
      lod: selectAtlasLOD(meshes, 'medium'),
      tiles: limitVolumeTiles(volumeTiles, { maxTiles: MAX_VOLUME_TILES }),
    }), { clock }));
    evidenceMeasurements.push(await measure('evidenceRetrieval', () => client.getEvidence(entityId), { clock }));
  }

  const boundedChunks = chunkItems(entityRows, { maxItems: MAX_VISIBLE_POINTS, chunkSize: MAX_POINTS_PER_CHUNK, getPosition: (item) => item.position });
  const boundedTiles = limitVolumeTiles(volumeTiles, { maxTiles: MAX_VOLUME_TILES });
  return {
    evidenceKind: 'legacy-helper-microbenchmark-not-interaction-or-GPU-evidence',
    fixture: { ...fixture, entityCount: entityRows.length },
    budgets: BENCHMARK_BUDGETS,
    metadata: {
      samples: metadataMeasurements.length,
      p95Ms: percentile(metadataMeasurements.map((item) => item.durationMs)),
      errors: metadataMeasurements.filter((item) => item.error).map((item) => item.error),
      capturedAt: new Date().toISOString(),
      userAgent: environment?.userAgent || 'unknown',
      devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      network: environment?.connection?.effectiveType || 'unknown',
    },
    firstSpatialResult: summary('firstSpatialResult', spatialMeasurements),
    pointChunks: {
      ...summary('selectionFeedback', pointMeasurements),
      inputEntities: entityRows.length,
      renderedEntities: boundedChunks.reduce((total, chunk) => total + chunk.length, 0),
      chunkCount: boundedChunks.length,
      maxChunkSize: Math.max(0, ...boundedChunks.map((chunk) => chunk.length)),
      bounded: entityRows.length <= 100000 && boundedChunks.length > 0 && boundedChunks.every((chunk) => chunk.length <= MAX_POINTS_PER_CHUNK),
    },
    meshVolumeDerivatives: {
      ...summary('summaryLoading', derivativeMeasurements),
      meshLod: selectAtlasLOD(meshes, 'medium')?.lod ?? null,
      volumeTileCount: boundedTiles.length,
      labelmapTileCount: boundedTiles.length,
      bounded: boundedTiles.length <= MAX_VOLUME_TILES,
    },
    evidenceRetrieval: summary('evidenceRetrieval', evidenceMeasurements),
    browserMatrix: BROWSER_MATRIX,
  };
}

/** Opt-in diagnostics only. Never record cells, request URLs, tokens or errors. */
export const MERFISH_BUDGETS = Object.freeze({spatialApi:1000, selectionHighlight:100, cachedEvidence:500, samples:30, cameraSeconds:30, medianFps:30, maxPoints:10000, maxExportRows:10000});
export function createMerfishBenchmark(metadata = {}) {
  const fields=['device','cpu','gpu','browser','viewport','release','checksum','build','server','cache','warmup','hardwareKind'];
  const environment=Object.fromEntries(fields.map(key=>[key,typeof metadata[key]==='string' && metadata[key].length<=160 && !/https?:|token=|bearer /i.test(metadata[key]) ? metadata[key] : 'unrecorded']));
  const counts=Object.fromEntries(['cohortCount','pointCount','renderLimit'].map(key=>[key,Number.isInteger(metadata[key])&&metadata[key]>=0&&metadata[key]<=10000?metadata[key]:null]));
  const samples={spatialApi:[],selectionHighlight:[],cachedEvidence:[]};
  let frames=[],cameraDurationMs=0;
  return {
    record(name, sample) {
      if (!Object.hasOwn(samples,name)) throw new TypeError('Unknown measurement family');
      if (samples[name].length>=300) throw new RangeError('Diagnostic sample limit');
      if (!['ok','failed','unavailable'].includes(sample?.outcome)) throw new TypeError('Explicit outcome required');
      const clean={outcome:sample.outcome};
      for(const field of ['durationMs','serverMs','networkMs','renderMs','bytes','encodedBytes']) {
        if(sample[field]===undefined)continue;
        if(typeof sample[field]!=='number'||!Number.isFinite(sample[field])||sample[field]<0)throw new TypeError('Invalid diagnostic number');
        clean[field]=sample[field];
      }
      if(clean.durationMs===undefined)throw new TypeError('Duration required, including failures');
      samples[name].push(clean);
    },
    camera(frameIntervals, durationMs) {
      if(!Array.isArray(frameIntervals)||frameIntervals.length>100000||frameIntervals.some(n=>typeof n!=='number'||!Number.isFinite(n)||n<=0)||!Number.isFinite(durationMs)||durationMs<0)throw new TypeError('Invalid frame samples');
      frames=[...frameIntervals];cameraDurationMs=durationMs;
    },
    report() {
      const latency=Object.fromEntries(Object.entries(samples).map(([name,rows])=>[name,{
        samples:rows.map(row=>({...row})),count:rows.length,failures:rows.filter(row=>row.outcome!=='ok').length,
        p95Ms:percentile(rows.map(row=>row.durationMs)),budgetMs:MERFISH_BUDGETS[name],
        pass:rows.length>=30&&rows.every(row=>row.outcome==='ok')&&percentile(rows.map(row=>row.durationMs))<=MERFISH_BUDGETS[name],
      }]));
      const medianFps=frames.length ? 1000/percentile(frames,.5) : null;
      const camera={frameIntervalsMs:[...frames],durationMs:cameraDurationMs,medianFps,pass:cameraDurationMs>=30000&&medianFps>=30};
      return {schema:'merfish-performance/v1',environment:{...environment},counts:{...counts},budgets:MERFISH_BUDGETS,latency,camera,
        targetDeviceGate:environment.hardwareKind!=='physical-target' || /swiftshader|llvmpipe|software/i.test(environment.gpu) || fields.some(key=>environment[key]==='unrecorded') || !counts.pointCount || counts.cohortCount===null || counts.renderLimit===null || counts.pointCount>counts.cohortCount || counts.pointCount>counts.renderLimit ? 'unverified' : Object.values(latency).every(item=>item.pass)&&camera.pass?'passed':'failed'};
    },
  };
}

/** Full-collection diagnostics are imported by the operator harness, never UI. */
export const COLLECTION_BUDGETS = Object.freeze({primitives:10000, tiles:64, spatialBytes:8*1024**2, page:100, genes:32, cacheBytes:128*1024**2, blockBytes:256*1024**2});
export function createCollectionBenchmark(metadata = {}) {
  const base = createMerfishBenchmark(metadata);
  const fullSource = metadata.sourceKind === 'published-full-collection';
  const detail = typeof metadata.detail === 'string' && /^[a-z0-9 .,:_-]{1,160}$/i.test(metadata.detail) ? metadata.detail : null;
  const counts = Object.fromEntries(['sourceCount','importedCount','spatialCount','publishedCount','geneCount'].map(k => [k, Number.isSafeInteger(metadata[k]) && metadata[k] >= 0 ? metadata[k] : null]));
  const digests = Object.fromEntries(['contentDigest','cellDigest','buildDigest'].map(k => [k, /^[a-f0-9]{64}$/.test(metadata[k]) ? metadata[k] : null]));
  const cold = [], cycles = [];
  let bounds = null, exported = null;
  const numeric = (input, fields) => Object.fromEntries(fields.map(k => [k, typeof input?.[k] === 'number' && Number.isFinite(input[k]) && input[k] >= 0 ? input[k] : null]));
  return {
    record: base.record, camera: base.camera,
    cold(family, sample) {
      if (!['query','order','summary'].includes(family) || cold.length >= 300) throw new TypeError('Invalid cold sample');
      cold.push({family, ...numeric(sample,['durationMs','serverMs','networkMs']), outcome:sample.outcome === 'ok' ? 'ok' : 'failed'});
    },
    cycle(sample) {
      if (cycles.length >= 300) throw new RangeError('Cycle limit');
      cycles.push(numeric(sample,['contextsCreated','contextsReleased','contextsLive','renderersCreated','renderersReleased','renderersLive','browserMemoryBytes','shaderErrors','contextErrors','navigationWrites','fullCohortFetches','fullCohortRebuilds']));
    },
    bounds(sample) { bounds = numeric(sample,Object.keys(COLLECTION_BUDGETS)); },
    export(sample) { exported = numeric(sample,['rows','genes','bytes','durationMs','peakBlockBytes','workerRssBytes','scratchBytes','fdsBefore','fdsAfter']); },
    report() {
      const legacy = base.report();
      const failures = [];
      if (Object.values(counts).some(v=>v===null) || counts.importedCount !== counts.publishedCount || counts.importedCount !== counts.sourceCount || counts.spatialCount > counts.importedCount || !counts.importedCount || !counts.geneCount) failures.push('population_unverified');
      if (Object.values(digests).some(v=>v===null)) failures.push('digests_missing');
      if (!detail) failures.push('lod_detail_missing');
      if (legacy.environment.hardwareKind !== 'physical-target' || /software|swiftshader|llvmpipe/i.test(legacy.environment.gpu) || Object.values(legacy.environment).includes('unrecorded')) failures.push('physical_device_missing');
      if (!Object.values(legacy.latency).every(v=>v.pass && v.samples.every(s=>['serverMs','networkMs','renderMs'].every(k=>s[k] !== undefined)))) failures.push('warm_latency');
      if (legacy.latency.spatialApi.samples.some(s=>s.bytes === undefined || s.encodedBytes === undefined || s.bytes>COLLECTION_BUDGETS.spatialBytes)) failures.push('spatial_bytes');
      if (!legacy.camera.pass || Math.abs(legacy.camera.frameIntervalsMs.reduce((a,b)=>a+b,0)-legacy.camera.durationMs)>1000) failures.push('camera');
      if (!bounds?.primitives || Object.entries(COLLECTION_BUDGETS).some(([k,v])=>bounds[k]===null || bounds[k]>v)) failures.push('bounds');
      if (cycles.length<20 || cycles.some(c=>Object.values(c).some(v=>v===null) || c.shaderErrors || c.contextErrors || c.navigationWrites>20 || c.fullCohortFetches || c.fullCohortRebuilds || c.contextsCreated-c.contextsReleased!==c.contextsLive || c.renderersCreated-c.renderersReleased!==c.renderersLive) || cycles.at(-1)?.contextsLive || cycles.at(-1)?.renderersLive) failures.push('lifecycle');
      if (!exported || Object.values(exported).some(v=>v===null) || exported.rows!==counts.importedCount || exported.genes!==counts.geneCount || exported.peakBlockBytes>COLLECTION_BUDGETS.blockBytes || exported.fdsAfter>exported.fdsBefore || exported.durationMs<=0) failures.push('complete_export');
      if (!['query','order','summary'].every(k=>cold.some(s=>s.family===k && s.durationMs!==null)) || cold.some(s=>s.outcome!=='ok')) failures.push('cold_work');
      // Source qualification is operator-supplied and must be backed by the pinned artifact.
      if (!fullSource) failures.push('representative_source_required');
      return {schema:'collection-performance/v1',environment:{...legacy.environment,detail},counts:{...counts},digests:{...digests},budgets:COLLECTION_BUDGETS,latency:legacy.latency,camera:legacy.camera,cold:structuredClone(cold),cycles:structuredClone(cycles),bounds:structuredClone(bounds),export:structuredClone(exported),releaseGate:failures.length ? 'blocked' : 'passed',failures};
    },
  };
}
